import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useAppStore } from '@repletoryreach/sdk-utils/store';
import { getConfig, useConfigStore } from '@repletoryreach/sdk-api';
import { RealtimeClient } from './client.js';

import type { HubName } from './events.js';

let _inboxClient: RealtimeClient<'inbox'> | null = null;
const _hubs = new Map<string, RealtimeClient<HubName>>();

export function getInboxClient(): RealtimeClient<'inbox'> | null {
  return _inboxClient;
}

/**
 * W6: generic accessor for additional hubs (broadcasts/flows). The provider
 * auto-starts the inbox hub; other hubs are registered explicitly by the
 * app once their backend endpoint exists.
 */
export function getHub<H extends HubName>(name: H): RealtimeClient<H> | null {
  return (_hubs.get(name) as RealtimeClient<H> | undefined) ?? null;
}

export function registerHub<H extends HubName>(name: H, client: RealtimeClient<H>): void {
  _hubs.set(name, client as unknown as RealtimeClient<HubName>);
}

export interface RealtimeProviderProps {
  children: ReactNode;
  /** Override the default behavior of starting the client when authenticated. */
  enabled?: boolean;
}

export function RealtimeProvider({ children, enabled }: RealtimeProviderProps) {
  const accessToken = useAppStore((s) => s.accessToken);
  const realtimeBaseUrl = useConfigStore((s) => s.realtimeBaseUrl);
  const inboxHubPath = useConfigStore((s) => s.inboxHubPath);
  const autoStart = useConfigStore((s) => s.realtime.autoStart);
  const verbose = useConfigStore((s) => s.realtime.verbose);
  const clientRef = useRef<RealtimeClient<'inbox'> | null>(null);

  const shouldStart = (enabled ?? autoStart) && Boolean(accessToken);
  const hubUrl = `${realtimeBaseUrl}${inboxHubPath}`;

  useEffect(() => {
    if (!shouldStart) {
      void clientRef.current?.stop();
      clientRef.current = null;
      _inboxClient = null;
      return;
    }
    const client = new RealtimeClient<'inbox'>({
      hubUrl,
      accessToken: () => useAppStore.getState().accessToken,
      verbose
    });
    clientRef.current = client;
    _inboxClient = client;
    let cancelled = false;
    void client.start().catch(() => {/* status already written by client */});
    return () => {
      cancelled = true;
      void client.stop();
      if (clientRef.current === client) {
        clientRef.current = null;
        _inboxClient = null;
      }
      void cancelled;
    };
  }, [shouldStart, hubUrl, verbose]);

  // A page opened with an expired sign-in fails its first connect (401) and SignalR does not retry a
  // connection that never opened. The console renews the sign-in on its next API call, so connect again
  // whenever the token changes and the client is down.
  useEffect(() => {
    const client = clientRef.current;
    if (!client || !accessToken || client.state !== 'disconnected') return;
    void client.start().catch(() => {/* status already written by client */});
  }, [accessToken]);

  return <>{children}</>;
}

void getConfig;
